import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Node_Type, TextStyle_TextAlignment } from 'ricos-schema';
import { createRicosFunctionalExtension } from 'wix-rich-content-plugin-commons';

import { getEnumByStyle } from './get-enum-by-style';
import { getStyleByEnum } from './get-style-by-enum';

type TextAlignOptions = {
  alignments: TextStyle_TextAlignment[];
  textAlignment: TextStyle_TextAlignment;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textAlign: {
      /**
       * Set the text align attribute
       */
      setTextAlign: (alignment: string) => ReturnType;
      unsetTextAlign: () => ReturnType;
    };
  }
}

export const textAlign = createRicosFunctionalExtension<TextAlignOptions>({
  type: 'extension',
  groups: [],
  name: 'textAlign',
  reconfigure(config, _extensions, ricosProps) {
    const types = [
      Node_Type.LIST_ITEM,
      Node_Type.PARAGRAPH,
      Node_Type.HEADING,
      Node_Type.CODE_BLOCK,
      Node_Type.BLOCKQUOTE,
    ];

    return {
      ...config,
      addOptions() {
        return {
          alignments: [
            TextStyle_TextAlignment.AUTO,
            TextStyle_TextAlignment.CENTER,
            TextStyle_TextAlignment.LEFT,
            TextStyle_TextAlignment.RIGHT,
            TextStyle_TextAlignment.JUSTIFY,
          ],
          textAlignment: getEnumByStyle(ricosProps.textAlignment, TextStyle_TextAlignment.AUTO),
        };
      },
      addGlobalAttributes() {
        return [
          {
            types,
            attributes: {
              textStyle: {
                default: this.options.textAlignment,
                parseHTML: element => {
                  const textAlignment = getEnumByStyle(
                    element.style.textAlign,
                    this.options.textAlignment
                  );

                  return {
                    textAlignment,
                  };
                },
                renderHTML: attributes => {
                  const textAlignment = attributes?.textStyle?.textAlignment
                    ? attributes.textStyle.textAlignment
                    : this.options.textAlignment;

                  return getStyleByEnum(textAlignment);
                },
              },
            },
          },
        ];
      },
      addCommands() {
        return {
          setTextAlign:
            (alignment: string) =>
            ({ commands }) => {
              const textAlignment = getEnumByStyle(alignment, TextStyle_TextAlignment.UNRECOGNIZED);
              if (!this.options.alignments.includes(textAlignment)) {
                console.error(
                  `invalid text alignment ${alignment} provided to setTextAlign command`
                );
                return false;
              }

              return types.every(type =>
                commands.updateAttributesWithDeepMerge(type, {
                  textStyle: { textAlignment },
                })
              );
            },
          unsetTextAlign:
            () =>
            ({ commands }) =>
              types.every(type =>
                commands.updateAttributesWithDeepMerge(type, {
                  textStyle: { textAlignment: this.options.textAlignment },
                })
              ),
        };
      },
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
      priority: 3,
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey('textAlign'),
            appendTransaction: (_transactions, oldState, newState) => {
              if (newState.doc === oldState.doc) {
                return;
              }
              const tr = newState.tr;
              const { from, to } = newState.selection;
              newState.doc.nodesBetween(from, to, (node, pos) => {
                if ([Node_Type.LIST_ITEM].includes(node.type.name as Node_Type)) {
                  const textStyle =
                    node.firstChild?.attrs.textStyle || TextStyle_TextAlignment.AUTO;

                  if (textStyle !== node.attrs.textStyle) {
                    tr.setNodeMarkup(pos, undefined, {
                      ...node.attrs,
                      textStyle,
                    });
                  }
                }
              });
              return tr;
            },
          }),
        ];
      },
    };
  },
});
